import React, { useState } from 'react';
import Checkbox from '@mui/material/Checkbox';
import {
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Box, Typography, TextField, IconButton, CircularProgress, Tooltip, useTheme, useMediaQuery
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Dropdown, { DropdownOption } from './ui/dropdown';
import { DatePicker } from '@mui/x-date-pickers';
import { useSettings } from '../providers/settings-provider';

import ActionMenu from './action-menu';
import Toggle from './ui/toggle';
import dayjs from 'dayjs';
import TablePlaceholder from './table-placeholder';
import { SlidersHorizontal } from 'lucide-react';
import { getDynamicFontSize } from '../util/font';
import CopyToClipboard from './copy-to-clipboard';

export type Column = {
    id: string;
    label: string;
    size?: number;
    type?: 'string' | 'number' | 'date' | 'dropdown' | 'toggle' | 'actions' | 'checkbox' | 'currency' | 'custom';
    customElement?: (value: any, row: Record<string, any>) => React.ReactNode;
    customHeader?: (col: Column) => React.ReactNode;
    inlineEdit?: boolean;
    options?: DropdownOption[];
    buttons?: { label: string; onClick: (row: Record<string, any>) => void }[];
    onToggle?: (row: Record<string, any>, value: boolean) => void;
    onSort?: (column: string) => void;
    onEdit?: (row: Record<string, any>) => void;
    onDelete?: (row: Record<string, any>) => void;
    canEdit?: (row: Record<string, any>) => boolean;
    canDelete?: (row: Record<string, any>) => boolean;
    editLabel?: string | ((row: Record<string, any>) => string);
    deleteLabel?: string | ((row: Record<string, any>) => string);
    showTime?: boolean;
    tooltip?: (row: Record<string, any>) => string | null;
    filterable?: boolean;
    filterComponent?: (open: boolean, anchorEl: HTMLElement | null, onClose: () => void, onApply: (filters: Record<string, any>) => void, currentFilter?: any) => React.ReactNode;
    isFilterActive?: (filterState?: any) => boolean;
    headerColor?: string;
    amountColor?: string;
    showTooltip?: boolean;
    showCheckbox?: boolean;
    canSelect?: (row: Record<string, any>) => boolean;
}

type AdditionalAction = {
    label: string | ((row: Record<string, any>) => string);
    icon?: 'edit' | 'delete' | 'add' | 'info' | 'download' | 'upload' | 'close' | 'save' | 'warning' | 'check' | 'user' | 'eye' | ((row: Record<string, any>) => 'edit' | 'delete' | 'add' | 'info' | 'download' | 'upload' | 'close' | 'save' | 'warning' | 'check' | 'user' | 'eye');
    onClick: (row: Record<string, any>) => void;
    visible?: (row: Record<string, any>) => boolean;
}

type DynamicTableProps = {
    columns: Column[];
    data: Record<string, any>[];
    title?: string;
    header?: React.ReactNode;
    additionalActions?: AdditionalAction[];
    contained?: boolean;
    sortState?: { column: string; direction: 'ASC' | 'DESC' };
    fontSx?: (row: Record<string, any>) => {
        fontSize?: number;
        fontWeight?: number;
        color?: string;
    };
    // Overrides the default (isTotal-highlight / zebra-stripe) row background for rows this returns a color for.
    rowBg?: (row: Record<string, any>) => string | undefined;
    customRender?: (row: Record<string, any>, columnId: string) => React.ReactNode;
    maxHeight?: number | string;
    maxWidth?: number | string;
    isLoading?: boolean;
    onSelectionChange?: (selectedIndices: number[]) => void;
    selectedIndices?: number[];
    columnFilters?: Record<string, any>;
    onColumnFilterChange?: (filters: Record<string, any>) => void;
    headerTextColor?: string;
    headerBg?: string;
    headerPadding?: number | string;
    headerRounded?: boolean;
    // Pins the first column to the left edge while the table scrolls horizontally.
    stickyFirstColumn?: boolean;
    // Pins the header row to the top of this table's own scroll container
    // while its rows scroll underneath - same mechanism the Transactions
    // table uses (a bounded-height container with overflowY: 'auto'; MRT's
    // header sticks to that same way). Pass `maxHeight` alongside this so
    // there's an actual bounded scroll container for it to stick within -
    // without one, `overflow-x: auto` on this table's own horizontal-scroll
    // wrapper implicitly turns its vertical overflow into a scroll container
    // too (a CSS quirk: an ancestor with overflow-x set can't have
    // overflow-y stay genuinely 'visible'), which traps page-level sticky
    // positioning and makes the header stick to nothing.
    stickyHeader?: boolean;
    // Offset from the top of this table's own scroll container (not the
    // page) - 0 unless this table is stacked below something else that's
    // also sticky within the SAME scroll container.
    stickyHeaderTop?: number;
    scrollContainerRef?: React.Ref<HTMLDivElement>;
    onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
};

const DynamicTable: React.FC<DynamicTableProps> = ({ columns, data, title, header, additionalActions, fontSx, rowBg, contained = true, customRender, sortState, maxHeight, maxWidth, isLoading = false, onSelectionChange, selectedIndices, columnFilters = {}, onColumnFilterChange, headerTextColor = 'var(--color-default-text)', headerBg = 'var(--color-container-background)', headerPadding = 12, headerRounded = true, stickyFirstColumn = false, stickyHeader = false, stickyHeaderTop = 0, scrollContainerRef, onScroll }) => {
    const theme = useTheme();
    // Same rationale as table-virtual.tsx's DynamicTableVirtual - below this
    // width this component's grid (e.g. forecast/budget-actual's category x
    // month matrix) just gets squeezed or forces horizontal scroll, so it
    // swaps to a card-per-row layout instead, reusing the same column
    // definitions/value-formatting as the desktop table.
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { formatDate, formatAmount } = useSettings();
    const [sort, setSort] = useState<{ column: string; direction: 'ASC' | 'DESC' }>({ column: '', direction: 'ASC' });
    const [editData, setEditData] = useState<Record<string, any>[]>(data);
    // Checkbox selection state
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [activeFilterPopup, setActiveFilterPopup] = useState<string>('');
    const filterRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

    // Use external sort state if provided, otherwise use internal state
    const currentSort = sortState || sort;

    React.useEffect(() => {
        setEditData(data);
        setSelectedRows(selectedIndices || []);
    }, [data, selectedIndices]);

    // Checkbox handlers
    const isAllSelected = editData.length > 0 && selectedRows.length === editData.length;
    const isIndeterminate = selectedRows.length > 0 && selectedRows.length < editData.length;
    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newSelection = e.target.checked ? editData.map((_, idx) => idx) : [];
        setSelectedRows(newSelection);
        onSelectionChange?.(newSelection);
    };
    const handleSelectRow = (idx: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const newSelection = e.target.checked 
            ? [...selectedRows, idx] 
            : selectedRows.filter(i => i !== idx);
        setSelectedRows(newSelection);
        onSelectionChange?.(newSelection);
    };

    const handleChange = (rowIdx: number, colId: string, value: any) => {
        setEditData(prev => prev.map((row, idx) =>
            idx === rowIdx ? { ...row, [colId]: value } : row
        ));
    };

    const handleFilterClick = (colId: string) => {
        const column = columns.find(c => c.id === colId);
        if (column?.filterable && column?.filterComponent) {
            if (activeFilterPopup === colId) {
                setActiveFilterPopup('');
            } else {
                setActiveFilterPopup(colId);
            }
        } else if (column?.onSort) {
            column.onSort(colId);
        }
    };

    const handleApplyFilter = (colId: string, filters: Record<string, any>) => {
        const newFilters = {
            ...columnFilters,
            [colId]: filters
        };
        onColumnFilterChange?.(newFilters);
        setActiveFilterPopup('');
    };

    const isColumnFilterActive = (column: Column): boolean => {
        if (column.isFilterActive) {
            return column.isFilterActive(columnFilters[column.id]);
        }
        return false;
    };

    // Shared between the desktop table cell renderer and the mobile card
    // list below.
    const renderActionsCell = (col: Column, row: Record<string, any>) => (
        <ActionMenu
            actions={[
                ...(col.onEdit && (!col.canEdit || col.canEdit(row)) ? [{
                    label: typeof col.editLabel === 'function' ? col.editLabel(row) : (col.editLabel || 'Edit'),
                    icon: 'edit' as const,
                    onClick: () => col.onEdit!(row)
                }] : []),
                ...(col.onDelete && (!col.canDelete || col.canDelete(row)) ? [{
                    label: typeof col.deleteLabel === 'function' ? col.deleteLabel(row) : (col.deleteLabel || 'Delete'),
                    icon: 'delete' as const,
                    onClick: () => col.onDelete!(row)
                }] : []),
                ...(additionalActions?.filter(action =>
                    action.visible ? action.visible(row) : true
                ).map(action => ({
                    label: typeof action.label === 'function' ? action.label(row) : action.label,
                    icon: typeof action.icon === 'function' ? action.icon(row) : action.icon,
                    onClick: () => action.onClick(row)
                })) || [])
            ]}
        />
    );

    // Shared between the desktop table cell renderer and the mobile card
    // list - every value-formatting branch (dropdown/date/toggle/currency/
    // tooltip etc.) lives here once instead of being duplicated.
    const renderCellValue = (col: Column, row: Record<string, any>, idx: number): React.ReactNode => {
        const cellValue = row[col.id];
        let content: React.ReactNode = null;
        if (col.customElement) {
            content = col.customElement(cellValue, row);
        } else if (col.type === 'custom') {
            content = customRender ? customRender(row, col.id) : null;
        } else if (col.type === 'dropdown' && col.inlineEdit) {
            content = (
                <Dropdown
                    options={col.options || []}
                    value={cellValue ?? ''}
                    onChange={val => { handleChange(idx, col.id, val); col.onEdit?.(row); }}
                    placeholder="Select..."
                    isPrimary
                    height={24}
                />
            );
        } else if (col.type === 'date' && col.inlineEdit) {
            content = (
                <DatePicker
                    value={cellValue ? dayjs(cellValue) : null}
                    onChange={val => handleChange(idx, col.id, val ? val.toISOString() : '')}
                    slotProps={{ textField: { fullWidth: true, size: 'small', variant: 'outlined' } }}
                />
            );
        } else if (col.type === 'toggle') {
            content = (
                <Box display="flex" alignItems="center" justifyContent="center">
                    <Toggle
                        checked={cellValue ?? false}
                        onChange={val => {
                            handleChange(idx, col.id, val);
                            col.onToggle && col.onToggle(row, val);
                        }}
                    />
                </Box>
            );
        } else if (col.inlineEdit) {
            content = (
                <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    type={col.type === 'number' ? 'number' : 'text'}
                    value={cellValue ?? ''}
                    onChange={e => handleChange(idx, col.id, col.type === 'number' ? Number(e.target.value) : e.target.value)}
                />
            );
        } else if (col.type === 'date') {
            const dateContent = <Typography sx={{ ...fontSx?.(row), fontWeight: (row as any).isTotal ? 700 : 400, pl: 1.25 }}>{cellValue ? formatDate(cellValue, col.showTime) : null}</Typography>;
            const tooltipText = col.tooltip ? col.tooltip(row) : null;
            content = tooltipText ? (
                <Tooltip
                    title={<Typography fontSize={13} color="black">{tooltipText}</Typography>}
                    slotProps={{ tooltip: { sx: { maxWidth: 400, fontSize: 13, p: 2, backgroundColor: 'rgba(255, 255, 255, 1)', boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)' } } }}
                    arrow
                >
                    <Box sx={{ cursor: 'pointer' }}>{dateContent}</Box>
                </Tooltip>
            ) : dateContent;
        } else if (col.type === 'currency') {
            const isNegative = cellValue < 0;
            const textColor = col.amountColor || (isNegative ? 'var(--color-negative-amount)' : 'var(--color-default-text)');
            const formatted = formatAmount ? formatAmount(cellValue) : String(cellValue);
            content = <Typography sx={{ ...fontSx?.(row), fontWeight: (row as any).isTotal ? 700 : 400, color: textColor }}>{formatted}</Typography>;
        } else {
            if (col.showTooltip) {
                content = (
                    <Tooltip title={<Box display="flex" alignItems="center" gap={2}><Typography fontSize={13} color="black">{cellValue}</Typography><CopyToClipboard text={cellValue} /></Box>}
                        slotProps={{ tooltip: { sx: { maxWidth: 400, fontSize: 13, p: 2, color: 'black', backgroundColor: 'rgba(255, 255, 255, 1)', boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)' } } }}>
                        <Typography sx={{ ...fontSx?.(row), fontWeight: (row as any).isTotal ? 700 : 400, pl: 1.25 }}>{cellValue}</Typography>
                    </Tooltip>
                );
            } else {
                content = <Typography sx={{ ...fontSx?.(row), fontWeight: (row as any).isTotal ? 700 : 400, pl: 1.25 }}>{cellValue}</Typography>;
            }
        }
        return content;
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TableContainer 
                component={contained ? Paper : 'div'} 
                sx={{ 
                    // A phone's card list needs only a little inset - the desktop 32px
                    // ate a fifth of the screen's width.
                    padding: contained ? (isMobile ? 1 : 4) : 0, 
                    borderRadius: 0, 
                    boxSizing: 'border-box',
                    paddingTop: contained 
                        ? (title || header) 
                            ? (isMobile ? 1 : 2) 
                            : (isMobile ? 1 : 4) 
                        : 0
                }}>
                { header ? header : title && <Box mb={2}><Typography variant="h6" color='var(--color-text-muted)'>{title}</Typography></Box>}

                {isMobile ? (
                    isLoading ? (
                        <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={8}>
                            <CircularProgress size={40} />
                            <Typography variant="body1" color="text.secondary">Loading data...</Typography>
                        </Box>
                    ) : editData?.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {editData.map((row, idx) => {
                                const displayColumns = columns.filter(c => c.type !== 'checkbox' && c.type !== 'actions');
                                const actionsCol = columns.find(c => c.type === 'actions');
                                const checkboxCol = columns.find(c => c.type === 'checkbox');
                                return (
                                    <Paper
                                        key={idx}
                                        sx={{
                                            p: 1.25,
                                            borderRadius: 2,
                                            background: rowBg?.(row) ?? ((row as any).isTotal ? `${theme.palette.primary.main}40` : 'var(--color-container-background)'),
                                            border: '1px solid rgba(0, 0, 0, 0.08)',
                                            boxSizing: 'border-box',
                                        }}
                                    >
                                        {(actionsCol || checkboxCol) && (
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb: displayColumns.length ? 1 : 0 }}>
                                                {checkboxCol && (
                                                    <Checkbox
                                                        checked={selectedRows.includes(idx)}
                                                        onChange={handleSelectRow(idx)}
                                                        sx={{ width: 20, height: 20, p: 0 }}
                                                    />
                                                )}
                                                {actionsCol && renderActionsCell(actionsCol, row)}
                                            </Box>
                                        )}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                            {displayColumns.map(col => (
                                                <Box key={col.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5 }}>
                                                    <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'var(--color-text-muted)', flexShrink: 0 }}>
                                                        {col.label}
                                                    </Typography>
                                                    <Box sx={{ textAlign: 'right', minWidth: 0, overflow: 'hidden' }}>
                                                        {renderCellValue(col, row, idx)}
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Paper>
                                );
                            })}
                        </Box>
                    ) : (
                        <TablePlaceholder
                            title="No Data Available"
                            message="No data available for the selected filters."
                        />
                    )
                ) : (
                <Box
                    ref={scrollContainerRef}
                    onScroll={onScroll}
                    sx={{ maxWidth: maxWidth, overflowX: maxWidth ? 'auto' : 'visible', overflowY: 'visible', boxSizing: 'border-box', ...(maxHeight && { maxHeight }), ...(maxHeight && { overflowY: 'auto' }) }}>
                <Table sx={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: '100%' }}>
                    <TableHead sx={{ backgroundColor: headerBg }}>
                        <TableRow>
                            {columns.map((col, colIdx) => {
                                const isStickyCol = stickyFirstColumn && colIdx === 0;
                                // The corner cell (both sticky) needs to out-rank both a
                                // scrolling row of other sticky-header cells passing
                                // underneath it AND the sticky-column body cells below it.
                                const stickyZIndex = isStickyCol && stickyHeader ? 4 : 3;
                                return (
                                <TableCell
                                    style={{
                                        fontWeight: 600,
                                        color: col.headerColor || headerTextColor,
                                        fontSize: getDynamicFontSize(col.label, col.size || 150, 12),
                                        width: col.size,
                                        cursor: col.onSort ? 'pointer' : undefined,
                                        userSelect: 'none',
                                        border: 'none',
                                        padding: headerPadding,
                                        backgroundColor: headerBg,
                                        // headerBg is often a translucent tint - a pinned/stuck cell
                                        // needs an opaque base underneath it or scrolled content
                                        // shows through.
                                        ...((isStickyCol || stickyHeader) && {
                                            position: 'sticky',
                                            ...(stickyHeader && { top: stickyHeaderTop }),
                                            ...(isStickyCol && { left: 0 }),
                                            zIndex: stickyZIndex,
                                            backgroundColor: 'var(--color-page-background)',
                                            // Twice: the other header cells get headerBg from both the
                                            // TableHead and their own cell, stacking a translucent tint.
                                            backgroundImage: `linear-gradient(${headerBg}, ${headerBg}), linear-gradient(${headerBg}, ${headerBg})`,
                                        }),
                                        whiteSpace: 'nowrap',
                                        borderRadius: !headerRounded ? 0 : col === columns[columns.length - 1] ? '0 8px 8px 0' : col === columns[0] ? '8px 0 0 8px' : 0,
                                    }}
                                    key={col.id}
                                >
                                    {col.customHeader ? (
                                        col.customHeader(col)
                                    ) : col.type === 'checkbox' && colIdx === 0 ? (
                                        (isIndeterminate || isAllSelected) ?
                                            <Checkbox
                                                indeterminate={isIndeterminate}
                                                checked={isAllSelected}
                                                onChange={handleSelectAll}
                                                sx={{
                                                    width: 5,
                                                    height: 5,
                                                    marginLeft: 0.5,
                                                }}
                                            /> :
                                            <div style={{ width: 5, height: 5, marginLeft: 0.5 }} />
                                    ) : col.type === 'toggle' ? (
                                        <Box display="flex" alignItems="center" justifyContent="center">
                                            {col.label}
                                        </Box>
                                    ) : col.type === 'currency' ? (
                                        <Box display="flex" alignItems="center" justifyContent="center">
                                            {col.label}
                                            {col.onSort && (
                                                <IconButton
                                                    size="small"
                                                    style={{ 
                                                        marginLeft: 4, 
                                                        color: currentSort.column === col.id ? 'var(--color-primary)' : '#B0B0B0',
                                                        backgroundColor: currentSort.column === col.id ? 'rgba(0, 182, 155, 0.1)' : 'transparent',
                                                        borderRadius: '50px',
                                                        width: 24,
                                                        height: 24,
                                                    }}
                                                    tabIndex={-1}
                                                    aria-label="Sort"
                                                    onClick={col.onSort ? () => {
                                                        if (!sortState) {
                                                            const newDirection = currentSort.column === col.id && currentSort.direction === 'ASC' ? 'DESC' : 'ASC';
                                                            setSort({ column: col.id, direction: newDirection });
                                                        }
                                                        col.onSort && col.onSort(col.id);
                                                    } : undefined}
                                                >
                                                    {currentSort.column === col.id ? (
                                                        currentSort.direction === 'ASC' 
                                                            ? <Typography sx={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--color-primary)' }}>▲</Typography> 
                                                            : <Typography sx={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--color-primary)' }}>▼</Typography>
                                                    ) : (
                                                        <Typography sx={{ fontSize: '12px', opacity: 0.4, color: 'var(--color-text-muted)' }}>▲</Typography>
                                                    )}
                                                </IconButton>
                                            )}
                                        </Box>
                                    ) : col.type === 'actions' ? (
                                        <Box display="flex" alignItems="center" justifyContent="flex-end">
                                            {col.label}
                                        </Box>
                                    ) : col.type === 'custom' ? (
                                        <Box display="flex" alignItems="center" justifyContent="center">
                                            {col.label}
                                        </Box>
                                    ) : (
                                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                            {col.label}
                                            {col.onSort && (
                                                <IconButton
                                                    size="small"
                                                    style={{ 
                                                        marginLeft: 4, 
                                                        color: currentSort.column === col.id ? 'var(--color-primary)' : '#B0B0B0',
                                                        backgroundColor: currentSort.column === col.id ? 'rgba(0, 182, 155, 0.1)' : 'transparent',
                                                        borderRadius: '50px',
                                                        width: 24,
                                                        height: 24,
                                                    }}
                                                    tabIndex={-1}
                                                    aria-label="Sort"
                                                    onClick={col.onSort ? () => {
                                                        if (!sortState) {
                                                            const newDirection = currentSort.column === col.id && currentSort.direction === 'ASC' ? 'DESC' : 'ASC';
                                                            setSort({ column: col.id, direction: newDirection });
                                                        }
                                                        col.onSort && col.onSort(col.id);
                                                    } : undefined}
                                                >
                                                    {currentSort.column === col.id ? (
                                                        currentSort.direction === 'ASC' 
                                                            ? <Typography sx={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--color-primary)' }}>▲</Typography> 
                                                            : <Typography sx={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--color-primary)' }}>▼</Typography>
                                                    ) : (
                                                        <Typography sx={{ fontSize: '12px', opacity: 0.4, color: 'var(--color-text-muted)' }}>▲</Typography>
                                                    )}
                                                </IconButton>
                                            )}
                                            {col.filterable && col.filterComponent && (
                                                <>
                                                    <IconButton
                                                        size="small"
                                                        style={{ 
                                                            marginLeft: 4, 
                                                            color: isColumnFilterActive(col) ? 'var(--color-primary)' : '#B0B0B0',
                                                            backgroundColor: isColumnFilterActive(col) ? 'rgba(0, 182, 155, 0.1)' : 'transparent',
                                                            borderRadius: '50px',
                                                            width: 24,
                                                            height: 24,
                                                        }}
                                                        tabIndex={-1}
                                                        aria-label="Filter"
                                                        onClick={() => handleFilterClick(col.id)}
                                                    >
                                                        <Box display="flex" alignItems="center">
                                                            { isColumnFilterActive(col) &&
                                                                <SlidersHorizontal size={12} color={isColumnFilterActive(col) ? 'var(--color-primary)' : 'var(--color-text-muted)'}/>
                                                            }
                                                            { !(currentSort.column === col.id) ?
                                                                <Typography sx={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--color-text-muted)', opacity: 0.4 }}>▲</Typography> :
                                                                <Typography sx={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--color-primary)' }}>{currentSort.direction === 'ASC' ? '▲' : '▼'}</Typography> 
                                                            }
                                                        </Box>
                                                    </IconButton>
                                                    <div ref={(el) => { if (el) filterRefs.current[col.id] = el; }}>
                                                        {col.filterComponent(
                                                            activeFilterPopup === col.id,
                                                            filterRefs.current[col.id],
                                                            () => setActiveFilterPopup(''),
                                                            (filters) => handleApplyFilter(col.id, filters),
                                                            columnFilters[col.id]
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </span>
                                    )}
                                </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} sx={{ textAlign: 'center', py: 8 }}>
                                    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                                        <CircularProgress size={40} />
                                        <Typography variant="body1" color="text.secondary">
                                            Loading data...
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : editData?.length > 0 ? editData?.map((row, idx) => {
                            const rowBackground = rowBg?.(row) ?? ((row as any).isTotal
                                ? `${theme.palette.primary.main}40`
                                : idx % 2 !== 0 ? 'var(--color-container-background)' : 'var(--color-page-background)');
                            return (
                            <TableRow
                                key={idx}
                                sx={{
                                    backgroundColor: rowBackground,
                                    height: 32,
                                    fontWeight: (row as any).isTotal ? 600 : 400,
                                }}
                            >
                                {columns.map((col, colIdx) => {
                                    const cellValue = row[col.id];
                                    if (col.type === 'checkbox' && colIdx === 0) {
                                        return (
                                            <TableCell key={col.id} sx={{ p: '4px 0', fontSize: 14, border: 'none', width: col.size }}>
                                                <Checkbox
                                                    checked={selectedRows.includes(idx)}
                                                    onChange={handleSelectRow(idx)}
                                                    sx={{ width: 5, height: 5 }}
                                                />
                                            </TableCell>
                                        );
                                    }
                                    if (col.type === 'actions') {
                                        return (
                                            <TableCell key={col.id} sx={{ p: '4px 0', fontSize: 14, border: 'none', width: col.size }}>
                                                <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                                                    {renderActionsCell(col, row)}
                                                </Box>
                                            </TableCell>
                                        );
                                    }
                                    const content = col.type === 'currency' ? (
                                        <Box display="flex" alignItems="center" justifyContent="flex-end" pr={2}>
                                            {renderCellValue(col, row, idx)}
                                        </Box>
                                    ) : renderCellValue(col, row, idx);
                                    return (
                                        <TableCell
                                            key={col.id}
                                            sx={{ 
                                                p: '4px 0', 
                                                fontSize: getDynamicFontSize(cellValue, col.size || 150, 12),
                                                border: 'none', 
                                                width: col.size,
                                                whiteSpace: col.type === 'string' && colIdx === 0 ? 'normal' : 'nowrap',
                                                textOverflow: 'ellipsis',
                                                overflow: 'hidden',
                                                ...(stickyFirstColumn && colIdx === 0 && {
                                                    position: 'sticky',
                                                    left: 0,
                                                    zIndex: 2,
                                                    backgroundColor: 'var(--color-page-background)',
                                                    backgroundImage: `linear-gradient(${rowBackground}, ${rowBackground})`,
                                                }),
                                            }}>
                                            {content}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            );
                        }) :
                            <TableRow>
                                <TableCell colSpan={columns.length}>
                                    <TablePlaceholder
                                        title="No Data Available"
                                        message="No data available for the selected filters."
                                    />
                                </TableCell>
                            </TableRow>
                        }
                    </TableBody>
                </Table>
                </Box>
                )}
            </TableContainer>
        </LocalizationProvider>
    );
};

export default DynamicTable;
